import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Crown, CheckCircle, Book, Loader2, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIPSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const sessionId = searchParams.get('session_id');
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');
  
  const [status, setStatus] = useState('processing');
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    if (paymentId && payerId && sessionId) {
      executePayment();
    }
  }, [paymentId, payerId, sessionId]);

  const executePayment = async () => {
    try {
      const res = await axios.get(`${API}/vip/execute-payment`, {
        params: { paymentId, PayerID: payerId, session_id: sessionId }
      });
      
      if (res.data.success) {
        setStatus('success');
        // Fetch membership details
        const statusRes = await axios.get(`${API}/vip/status/${sessionId}`);
        setMembership(statusRes.data.membership);
      }
    } catch (error) {
      console.error('Payment execution error:', error);
      setStatus('error');
    }
  };

  if (status === 'processing') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <Loader2 className="animate-spin mx-auto mb-4 text-amber-500" size={48} />
          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Activating your VIP membership...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className={`max-w-md mx-auto p-8 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-xl text-center`}>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-500 text-3xl">!</span>
          </div>
          <h1 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Payment Issue</h1>
          <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            There was an issue processing your payment. Please try again or contact support.
          </p>
          <Link to={`/my-matches/${sessionId}`}>
            <Button className="bg-[#004E86] hover:bg-[#003d6b] text-white">
              Return to Matches
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen py-12 ${isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-amber-50 to-white'}`} data-testid="vip-success-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Success Card */}
        <div className={`rounded-2xl overflow-hidden shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white p-8 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Crown size={40} />
            </div>
            <h1 className="text-3xl font-bold mb-2">Welcome to VIP!</h1>
            <p className="text-amber-100">Your membership is now active</p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* What's Included */}
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              What's Included:
            </h2>
            
            <div className="space-y-4 mb-8">
              <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-50'}`}>
                <CheckCircle className="text-green-500 shrink-0" size={24} />
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Daily RV Matches</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Starting tomorrow, you'll receive personalized RV matches based on your quiz preferences every morning.
                  </p>
                </div>
              </div>

              <div className={`flex items-start gap-3 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <Mail className="text-blue-500 shrink-0" size={24} />
                <div>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Welcome Email Sent</p>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Check your inbox! We've sent ebook access links and welcome information.
                  </p>
                </div>
              </div>
            </div>

            {/* Ebook Access */}
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Your Ebooks:
            </h2>
            
            <div className="grid gap-4 mb-8">
              <Link to={`/ebook/rv-buying-guide?token=${membership?.id || sessionId}`}>
                <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:scale-[1.02] ${isDark ? 'border-gray-600 hover:border-amber-500 bg-gray-700' : 'border-gray-200 hover:border-amber-400 bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <Book className="text-amber-500" size={24} />
                    <div>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>RV Buying Guide</p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Complete guide to purchasing your RV</p>
                    </div>
                  </div>
                  <ArrowRight className={isDark ? 'text-gray-400' : 'text-gray-400'} size={20} />
                </div>
              </Link>

              <Link to={`/ebook/rv-travel-guide?token=${membership?.id || sessionId}`}>
                <div className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all hover:scale-[1.02] ${isDark ? 'border-gray-600 hover:border-amber-500 bg-gray-700' : 'border-gray-200 hover:border-amber-400 bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <Book className="text-blue-500" size={24} />
                    <div>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>RV Travel Guide</p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Destinations, tips & life on the road</p>
                    </div>
                  </div>
                  <ArrowRight className={isDark ? 'text-gray-400' : 'text-gray-400'} size={20} />
                </div>
              </Link>
            </div>

            {/* Back to Matches */}
            <div className="text-center">
              <Link to={`/my-matches/${sessionId}`}>
                <Button className="bg-[#004E86] hover:bg-[#003d6b] text-white">
                  View Your RV Matches
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VIPSuccessPage;
